<script setup>
import { ref, watch, onMounted, onUnmounted, computed } from 'vue'
import { gApp, } from '../State.js'
import { useRoute } from "vue-router"
import SignedInView from './SignedInView.vue'
import MenuLink from './MenuLink.vue'
import QuickHelpSection from './QuickHelpSection.vue'
import NewBadge from './utils/NewBadge.vue'

const route = useRoute();

let projectLoadError = ref(null);
let isHelpExpanded = ref(false);
const license = computed(() => gApp.getLicense());

async function fetchData(newId) {
  projectLoadError.value = null;
  let canOpen = await gApp.canOpenProj(newId);
  if (!canOpen) {
    // If do not have access to this project, return to home
    console.log("Project is locked by someone else! Returning home.");
    gApp.router.replace('/');
    return;
  }
  let projResult = await gApp.setCurProj(newId);
  if (projResult.isError()) {
    projectLoadError.value = projResult.getError();
    return;
  }
  projectLoadError.value = null;
  updateHelpIdForPath(route.fullPath);
}

function updateHelpIdForPath(newPath) {
  if (gApp.proj()) {
    gApp.proj().setHelpIdForPath(route.fullPath);
  }
}

watch(() => {
    return route.params.id;
  },
  (newId) => {
    fetchData(newId);
  }, {immediate: true});

watch(() => {
  return route.fullPath;
},
(newPath) => {
  updateHelpIdForPath(newPath);
}, {immediate: true})

// Watch for license changes and redirect to home if license becomes inactive
watch(() => license.value, (newLicense) => {
  if (newLicense && !newLicense.isActive()) {
    console.log("License is inactive, redirecting to home");
    gApp.router.replace('/');
  }
}, { immediate: true })

onMounted(() => {
  console.log("Mounting ProjectView");
})

onUnmounted(() => {
  console.log("Unmounting ProjectView!");
  // Save + release the project (no longer editing)
  if (gApp.proj()) {
    gApp.proj().save();
  }
})

</script>

<template>
  <SignedInView class="ProjectView">
    <div class="Sidebar PaddingL">
      <template v-if="gApp.proj() !== null">
        <MenuLink absPath="/" class="BackToHomeBtn">
          <i class="bi-arrow-left MRXS"></i>
          <span class="BackBtnText">All Projects</span>
        </MenuLink>

        <MenuLink relPath="" :class="{'GuideTab': !gApp.proj().isGuideComplete()}">
          <span class="TextSpan">
            <i class="bi-asterisk MRXS"></i>Guide
          </span>
        </MenuLink>
        <MenuLink relPath="assistant"><i class="bi-stars MRXS"></i>Assistant<NewBadge text="BETA" class="ml-xs" /></MenuLink>

        <p class="mute-text small-upper SidebarHeader">
          {{gApp.proj().isCommercial() ? 'Building' : 'House'}}
        </p>
        <template v-if="gApp.proj().isCommercial()">
          <MenuLink relPath="location"><i class="bi-geo-alt MRXS"></i>Location</MenuLink>
          <MenuLink relPath="spaces"><i class="bi-box MRXS"></i>Spaces</MenuLink>
          <MenuLink relPath="systems"><i class="bi-grid MRXS"></i>Systems</MenuLink>
          <MenuLink relPath="results"><i class="bi-bar-chart-line MRXS"></i>Results</MenuLink>
        </template>
        <template v-else>
          <MenuLink relPath="build"><i class="bi-box MRXS"></i>Build House</MenuLink>
          <MenuLink relPath="results"><i class="bi-bar-chart-line MRXS"></i>Results</MenuLink>
        </template>

        <p class="mute-text small-upper pl-m SidebarHeader">Components</p>
        <MenuLink relPath="walls"><i class="bi-bricks MRXS"></i>Walls</MenuLink>
        <MenuLink relPath="roofs"><i class="bi-house-fill MRXS"></i>Roofs</MenuLink>
        <MenuLink relPath="windows"><i class="bi-grid-fill MRXS"></i>Windows</MenuLink>
        <MenuLink relPath="doors"><i class="bi-door-closed-fill MRXS"></i>Doors</MenuLink>
        <MenuLink relPath="skylights"><i class="bi-brightness-high-fill MRXS"></i>Skylights</MenuLink>
        <MenuLink relPath="internalshadings"><i class="bi-shadows MRXS"></i>Internal Shadings</MenuLink>
        <MenuLink relPath="externalshadings"><i class="bi-shadows MRXS"></i>External Shadings</MenuLink>
        <template v-if="gApp.proj().isCommercial()">
          <MenuLink relPath="bufferspaces"><i class="bi-distribute-horizontal MRXS"></i>Buffer Spaces</MenuLink>
          <MenuLink relPath="schedules"><i class="bi-alarm MRXS"></i>Schedules</MenuLink>
        </template>

        <p class="mute-text small-upper pl-m SidebarHeader mt-s">Tools</p>
        <MenuLink relPath="import"><i class="bi-download MRXS"></i>Import</MenuLink>

        <p class="mute-text small-upper pl-m SidebarHeader mt-s">Settings</p>
        <MenuLink relPath="settings"><i class="bi-gear MRXS"></i>Settings</MenuLink>
      </template>
    </div>
    <div class="RightSidebar" :class="{ 'expanded': isHelpExpanded }" v-if="gApp.proj() && gApp.proj().helpPaneVisible">
      <div class="HelpHeader">
        <!-- <button class="LinkBtn float-r" @click="gApp.proj().setHelpPaneVisible(false)">Hide</button> -->
        <h3 class="QuickHelpTitle"><i class="mr-xxs bi-lightning-charge"></i>Quick Help</h3>
        <button class="LinkBtn ExpandBtn" @click="isHelpExpanded = !isHelpExpanded">
          <i :class="isHelpExpanded ? 'bi-arrows-angle-contract' : 'bi-arrows-angle-expand'"></i>
        </button>
        <button class="LinkBtn CloseX" @click="gApp.proj().setHelpPaneVisible(false)">&#x2715;</button>
      </div>
      <div id="HelpContent">
        <QuickHelpSection :id="gApp.proj() ? gApp.proj().getHelpId() : null" />
      </div>
    </div>
    <div class="Main PaddingM">
      <button class="QuickHelpOpener CreateBtn" @click="gApp.proj().setHelpPaneVisible(true)"><i class="bi-lightning-charge mr-xxs"></i>Quick Help</button>
      <div v-if="gApp.proj() !== null">
        <!-- <router-view></router-view> -->
        <!-- This keeps the page alive when changing pages -->
        <router-view v-slot="{ Component }">
          <keep-alive>
            <component :is="Component" />
          </keep-alive>
        </router-view>
      </div>
      <div v-else>
        <div v-if="!projectLoadError" class="project-loading-container">
          <div class="project-loading-spinner"></div>
          <h4 class="project-loading-text">Loading project<span class="dot-1">.</span><span class="dot-2">.</span><span class="dot-3">.</span></h4>
        </div>
        <div v-else>
          <h4 class="upper mb-m">Error loading project</h4>
          <p class="w-60 mb-m">{{projectLoadError}}</p>
          <p class="mb-l">Please verify that this project exists and you have access to it.</p>
          <p class=""><i>Return to <router-link to="/">Projects</router-link></i></p>
        </div>
      </div>
    </div>
  </SignedInView>
</template>

<style scoped>
.ProjectView {
  /* display: grid; */
  /* grid-template-columns: var(--sidebar-w) 1fr; */
  /* gap: var(--space-m); */
  height: 100%;
}

.Sidebar {
  width: var(--sidebar-w);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  height: 100%;
  padding: var(--space-xs) 0 80px 0;
  background: var(--sidebar-bg);
  color: var(--sidebar-color);
  position: fixed;
  float: left;
  font-size: var(--f-m);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.Sidebar::-webkit-scrollbar {
  width: 4px;
}

.Sidebar::-webkit-scrollbar-track {
  background: transparent;
}

.Sidebar::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 2px;
}

/* webkit browsers(safari, chrome) */
/* 
*::-webkit-scrollbar {
  border: none;
  background-color: transparent;
} 
*/
/* firefox */
/*
*{
  scrollbar-color: #404040b3 transparent;
}
*/

.RightSidebar {
  position: fixed;
  right: 0;
  top: var(--header-height);
  z-index: 1000;
  /*width: 400px;*/
  width: 500px;
  max-width: 25%;
  height: calc(100vh - var(--header-height));
  background-color: var(--main-bg);
  /* padding: var(--s); */
  border-left: 1px solid grey;
  transition: all 0.3s ease;
  transform: translateX(0);
}

.RightSidebar.expanded {
  width: 800px;
  max-width: 60%;
}

.HelpHeader {
  border-bottom: 1px solid grey;
  padding: var(--s);
  display: flex;
  align-items: center;
  gap: var(--space-xs);
}

.QuickHelpTitle {
  margin-right: auto;
}

.ExpandBtn {
  background: none;
  border: none;
  padding: var(--space-xxs);
  border-radius: var(--br-s);
  cursor: pointer;
  color: var(--pc);
  font-size: var(--f-m);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  transition: all 0.2s ease;
}

.ExpandBtn:hover {
  background-color: var(--pc-light);
  color: var(--pc-dark);
}

.CloseX {
  background: none;
  border: none;
  padding: var(--space-xxs);
  border-radius: var(--br-s);
  cursor: pointer;
  color: var(--pc);
  font-size: var(--f-m);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  transition: all 0.2s ease;
}

.CloseX:hover {
  background-color: var(--delete-red);
  color: white;
}

#HelpContent {
  padding: var(--s);
  padding-bottom: var(--xxl);
  height: 100%;
  overflow-y: scroll;
  scrollbar-width: thin;
}

.SidebarHeader {
  color: var(--pc-med2);
  font-weight: 600;
  letter-spacing: 1px;
  margin-top: var(--m);
  margin-bottom: var(--xs);
  padding-left: var(--m);
}

.QuickHelpOpener {
  float: right;
}

.Main {
  width: 100%;
  padding-left: calc(var(--sidebar-w) + var(--space-l));
  /* padding-left: var(--space-xl); */
}

.BackToHomeBtn {
  margin-top: var(--xs);
  margin-bottom: var(--m);
  background-color: rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: var(--xs) var(--s);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  width: 90%;
  margin-left: auto;
  margin-right: auto;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.BackToHomeBtn:hover {
  background-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
  border-color: rgba(255, 255, 255, 0.15);
}

.BackToHomeBtn i {
  font-size: 1.3em;
  transition: all 0.2s ease;
  color: rgba(255, 255, 255, 0.9);
}

.BackToHomeBtn:hover i {
  transform: scale(1.1);
  color: white;
}

.BackBtnText {
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
}

.BackToHomeBtn:hover .BackBtnText {
  color: white;
}

.GuideTab {
  background-color: rgba(255, 255, 255, 0.12);
  border-left: 4px solid rgba(255, 193, 7, 0.9);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.25);
  position: relative;
  font-weight: 700;
}

.GuideTab:hover {
  background-color: rgba(255, 255, 255, 0.2);
  border-left-color: rgba(255, 193, 7, 1);
}

.GuideTab::after {
  content: '';
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgba(255, 193, 7, 0.9);
}

.project-loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--xl);
  margin: var(--m) 0;
  min-height: 250px;
  border-radius: var(--br-m);
}

.project-loading-spinner {
  width: 60px;
  height: 60px;
  border: 6px solid rgba(62, 85, 204, 0.3);
  border-radius: 50%;
  border-top-color: var(--pc);
  border-left-color: var(--pc);
  animation: spin 1s ease-in-out infinite;
  margin-bottom: var(--s);
}

.project-loading-text {
  font-size: var(--f-m);
  color: var(--pc);
  font-weight: 600;
  animation: pulse 2s infinite;
  text-transform: uppercase;
  letter-spacing: 1px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
}

.dot-1, .dot-2, .dot-3 {
  animation: dotPulse 1.5s infinite;
  display: inline-block;
}

.dot-1 { animation-delay: 0s; }
.dot-2 { animation-delay: 0.3s; }
.dot-3 { animation-delay: 0.6s; }

@keyframes dotPulse {
  0% { opacity: 0; transform: translateY(0); }
  50% { opacity: 1; transform: translateY(-5px); }
  100% { opacity: 0; transform: translateY(0); }
}

/* Only shows when HelpContent is completely empty */
#HelpContent:empty::before {
  content: "There is no help content for this page. If you need assistance, click 'Get Help' in the top-right.";
  color: #888;
  font-style: italic;
}


</style>
